// The Big Knock's page glue (sprint 29 item 3): keeps the server's state with its own clock, shows the hall banner, the door panel, the wait
// and the reveal, and sends the few messages the event needs. No DOM of its own: the screens are client/bigknock.js. Everything it needs from
// the page comes in as a hook, so a test can drive it with fakes.
import { BIGKNOCK_COPY as C } from '../game/bigknock-copy.js';
import { leftOf, clockLine, shownPhase, nextLine, renderBanner, renderPanel, renderWaiting, renderReveal, CROWD_EVERY_MS } from './bigknock.js';

export const STRIP_MS = 60000; // a reveal behind a live door round opens by itself after this long

export function createBigKnockUi({ doc, banner, root, send, perf = () => performance.now(), reduced = () => false, hall = () => true, topOf = () => 0, onShare = () => {}, onBack = () => {}, notify = () => {},
  startLine = () => {}, visitor = null, now = () => Date.now(), tzAt, zoneAt = null, set, clear }) {
  let st = null, next = null, mode = null, called = null, pending = null, panel = null, wait = null, reveal = null, pollAt = 0, shownN = 0, told = '', line = null, stripOn = false, stripTimer = null;
  const bannerView = renderBanner(banner, { doc, onSee: () => open() });
  const left = () => leftOf(st, perf());

  function show(m) { mode = m; root.hidden = !m; }
  function open() {
    if (!st) return;
    if (st.joined && called) return openWait();
    panel = renderPanel(root, { doc, onKnock: () => send({ t: 'bk-join' }), onClose: () => close() });
    show('panel'); refresh(); send({ t: 'bk-state' });
    panel.knock.focus?.();
  }
  // "Play the door while you wait": the wait gives way to the door, the banner (the clock, See) stays; the reveal still comes by itself
  function openWait() {
    wait = renderWaiting(root, { doc, onPlay: () => close(), now, tzAt, zoneAt });
    show('wait'); refresh();
    wait.play.focus?.();
  }
  function close() {
    if (mode === 'reveal') { reveal?.cancel(); send({ t: 'bk-seen' }); pending = null; endStrip(); }
    show(null); panel = wait = reveal = null; refresh(); onBack();
  }
  // A reveal that lands while a door round is live waits behind a one-line strip in the banner's slot; it opens when the round ends, on a tap, or after 60 s
  function startStrip() {
    if (stripOn || !pending) return;
    stripOn = true;
    bannerView.strip(pending.n, () => showReveal(true));
    stripTimer = set(() => { stripTimer = null; if (pending) showReveal(true); }, STRIP_MS);
    refresh();
  }
  function endStrip() { if (!stripOn) return; stripOn = false; if (stripTimer !== null) clear(stripTimer); stripTimer = null; }
  function showReveal(force = false) {
    if (!pending || (!hall() && !force)) return;
    const m = pending; pending = null; shownN = m.n; endStrip();
    reveal?.cancel();
    reveal = renderReveal(root, m, { doc, reduced: reduced(), onShare: () => onShare(m), onClose: () => close(), set, clear, visitor, now, tzAt, zoneAt });
    show('reveal');
    reveal.back.focus?.();
    refresh();
  }
  // the start dialog's one quiet line (the next window) while nothing is announced
  function quiet() {
    const text = !st && next && hall() && !stripOn ? nextLine(next, now(), tzAt, zoneAt) : '';
    if (text !== line) { line = text; startLine(text); }
  }
  function refresh() {
    doc.documentElement?.style?.setProperty?.('--bk-top', `${topOf()}px`);
    quiet();
    if (!st) { banner.hidden = !stripOn; if (mode === 'panel' || mode === 'wait') show(null); return; }
    const l = left();
    if (mode === 'panel' && panel) panel.set({ ...st, phase: shownPhase(st, l) === 'open' ? 'open' : st.phase }, l);
    if (mode === 'wait' && wait) wait.set(st, l, called ?? { counts: true }, next);
    banner.hidden = !((hall() && !mode) || stripOn);
    if (!banner.hidden && !stripOn) bannerView.set(st, l);
  }

  return {
    // the server's picture of the event: null when there is nothing to show
    onState(s) {
      st = s ? { ...s, at: perf() } : null;
      if (st && !st.joined) called = null;
      if (st?.called && !called) called = { counts: true };
      // a player in a door or at a reveal does not see the banner: one line in the chat says it is on
      if (st && !hall() && (st.phase === 'announced' || st.phase === 'open') && told !== st.n + st.phase) { told = st.n + st.phase; notify(C.banner.title(st.n) + '. ' + clockLine(st, left()) + '.'); }
      refresh();
    },
    // the public window for the next event ({ n, from, to }, nothing of its drawn gap), or null
    onNext(w) { next = w ?? null; refresh(); },
    // a Big Knock round began: the overlay gives way to the door
    onRound() { called = null; show(null); panel = wait = null; refresh(); },
    onCalled(m) { called = m; if (st) { st = { ...st, called: true, joined: true }; } openWait(); },
    onReveal(m) { pending = m; if (hall()) showReveal(); else startStrip(); },
    error(text) { if (mode === 'panel' && panel) { panel.say(text); return true; } return false; },
    // once a second: the clocks; every few seconds with the panel or the wait open, a fresh head count
    tick() {
      refresh();
      if (pending && mode !== 'reveal') showReveal(); // the reveal takes the place of the panel or the wait, as soon as no door is live
      const t = perf();
      if ((mode === 'panel' || mode === 'wait') && t - pollAt >= CROWD_EVERY_MS) { pollAt = t; send({ t: 'bk-state' }); }
    },
    open, close,
    get mode() { return mode; }, get state() { return st; }, get shown() { return shownN; },
  };
}
